import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HStack, Input, RadioCard, Stack, Text, } from '@chakra-ui/react';
import { FormField, FormInput, } from '@client/components/input/FormField';
import { AppDialog } from '@client/components/misc/AppDialog';
import { DialogConfirmFooter } from '@client/components/misc/DialogConfirmFooter';
import { RichTextDisplay } from '@client/components/richText/RichTextDisplay';
import { RichTextEditor } from '@client/components/richText/RichTextEditor';
import { STANDARD_RETURN_POLICY_HTML } from '@client/constants';
import { sansFontFamily } from '@client/theme';
import { ReturnPolicyType } from '@heirloom/common/constants';
import { validateReturnProfileInput } from '@heirloom/common/validation/profiles';
import { ValidationField, } from '@heirloom/common/validation/shared';
import { useEffect, useRef, useState } from 'react';
export const ReturnProfileDialog = ({ open, onClose, onConfirm, existingNames = [], saving = false, }) => {
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(null);
    const [windowDays, setWindowDays] = useState('');
    const [windowError, setWindowError] = useState(null);
    const [policyType, setPolicyType] = useState(ReturnPolicyType.STANDARD);
    const customHtmlRef = useRef('');
    const [customTextError, setCustomTextError] = useState(null);
    useEffect(() => {
        if (!open) {
            setName('');
            setNameError(null);
            setWindowDays('');
            setWindowError(null);
            setPolicyType(ReturnPolicyType.STANDARD);
            customHtmlRef.current = '';
            setCustomTextError(null);
        }
    }, [open]);
    const isDirty = () => !!name ||
        !!windowDays ||
        policyType !== ReturnPolicyType.STANDARD ||
        !!customHtmlRef.current;
    const handleClose = () => {
        if (isDirty()) {
            if (!window.confirm('Discard changes?'))
                return;
        }
        onClose();
    };
    const handleConfirm = () => {
        const errors = validateReturnProfileInput({
            name,
            existingNames,
            policyType,
            returnWindowDays: windowDays || null,
            policyDescrRichText: customHtmlRef.current,
        });
        const findError = (field) => errors.find((e) => e.field === field)?.message ?? null;
        setNameError(findError(ValidationField.Name));
        setWindowError(findError(ValidationField.WindowDays));
        setCustomTextError(findError(ValidationField.CustomText));
        if (errors.length > 0)
            return;
        const trimmedName = name.trim();
        const days = policyType !== ReturnPolicyType.NO_RETURNS
            ? parseInt(windowDays, 10)
            : undefined;
        const policy = policyType === ReturnPolicyType.NO_RETURNS
            ? { type: ReturnPolicyType.NO_RETURNS }
            : policyType === ReturnPolicyType.STANDARD
                ? { type: ReturnPolicyType.STANDARD }
                : {
                    type: ReturnPolicyType.CUSTOM,
                    text: customHtmlRef.current,
                };
        void onConfirm({
            name: trimmedName,
            windowDays: days,
            policy,
        });
    };
    return (_jsx(AppDialog, { title: "New Return Profile", open: open, onCancel: handleClose, pending: saving, size: "md", footer: _jsx(DialogConfirmFooter, { onCancel: handleClose, onConfirm: handleConfirm, confirmLabel: "Create", pending: saving }), children: _jsxs(Stack, { gap: 4, children: [_jsx(FormField, { label: "Name", error: nameError, required: true, children: _jsx(FormInput, { value: name, onChange: (e) => {
                            setName(e.target.value);
                            if (e.target.value.trim())
                                setNameError(null);
                        }, placeholder: "e.g. Standard Returns", disabled: saving }) }), _jsx(HStack, { align: "start", gap: 0, children: _jsx(FormField, { label: "Policy", children: _jsx(RadioCard.Root, { value: policyType, onValueChange: (e) => setPolicyType(e.value), size: "sm", alignSelf: "stretch", disabled: saving, children: _jsxs(HStack, { gap: 3, children: [_jsxs(RadioCard.Item, { value: ReturnPolicyType.STANDARD, children: [_jsx(RadioCard.ItemHiddenInput, {}), _jsxs(RadioCard.ItemControl, { alignItems: "center", children: [_jsx(RadioCard.ItemText, { fontSize: 16, children: "Standard" }), _jsx(RadioCard.ItemIndicator, {})] })] }), _jsxs(RadioCard.Item, { value: ReturnPolicyType.CUSTOM, children: [_jsx(RadioCard.ItemHiddenInput, {}), _jsxs(RadioCard.ItemControl, { alignItems: "center", children: [_jsx(RadioCard.ItemText, { fontSize: 16, children: "Custom" }), _jsx(RadioCard.ItemIndicator, {})] })] }), _jsxs(RadioCard.Item, { value: ReturnPolicyType.NO_RETURNS, children: [_jsx(RadioCard.ItemHiddenInput, {}), _jsxs(RadioCard.ItemControl, { alignItems: "center", children: [_jsx(RadioCard.ItemText, { fontSize: 16, children: "No returns" }), _jsx(RadioCard.ItemIndicator, {})] })] })] }) }) }) }), policyType !== ReturnPolicyType.NO_RETURNS && (_jsx(FormField, { label: "Window", error: windowError, width: 200, required: true, children: _jsxs(HStack, { gap: 3, align: "center", children: [_jsx(Input, { size: "lg", w: 16, fontSize: 16, fontFamily: sansFontFamily, value: windowDays, onChange: (e) => {
                                    setWindowDays(e.target.value.replace(/\D/g, ''));
                                    setWindowError(null);
                                }, inputMode: "numeric", placeholder: "30", disabled: saving }), _jsx(Text, { fontSize: 18, children: "Days" })] }) })), policyType === ReturnPolicyType.STANDARD && (_jsx(RichTextDisplay, { htmlString: STANDARD_RETURN_POLICY_HTML, fontSize: 18 })), policyType === ReturnPolicyType.CUSTOM && (_jsx(FormField, { label: "Details", required: true, children: _jsx(RichTextEditor, { onChange: (html) => {
                            customHtmlRef.current = html;
                            if (html
                                .replace(/<[^>]*>/g, '')
                                .trim())
                                setCustomTextError(null);
                        }, invalid: !!customTextError, error: customTextError, maxHeight: 150, disabled: saving }) }))] }) }));
};
